import type { Database, Json } from "~~/types/supabase";

// The waitlist of an activity (CLAUDE.md, "Waitlist"): events.additional_settings.waitlist, read
// with the same defaults as waitlist_settings() in SQL. Seated activities have no waitlist yet.
export type WaitlistMode = "auto" | "manual";
export type WaitlistHold = 2 | 12 | 24;
export interface WaitlistSettings {
  enabled: boolean;
  mode: WaitlistMode;
  hold_hours: WaitlistHold;
  any_timeslot: boolean;
  before_sale: boolean;
  // the shop offers it per sold-out ticket type; otherwise once everything on sale is sold out
  per_ticket_type: boolean;
  // an optional phone field in the shop, for a place that comes free at the last moment
  ask_phone: boolean;
}
export type WaitlistStatus = "waiting" | "offered" | "bought" | "expired" | "left" | "removed" | "notified";

export const WAITLIST_HOLDS: WaitlistHold[] = [2, 12, 24];
// What switching it on writes: automatic, a place held for 12 hours, one timeslot per sign-up,
// only once everything sold out.
export const WAITLIST_ON: WaitlistSettings = { enabled: true, mode: "auto", hold_hours: 12, any_timeslot: false, before_sale: false, per_ticket_type: false, ask_phone: false };

type EventLike = { additional_settings?: unknown; seating_plan_id?: string | null } | null | undefined;

export const waitlistSettingsOf = (event: EventLike): WaitlistSettings => {
  const w = ((event?.additional_settings as { waitlist?: Partial<WaitlistSettings> } | null)?.waitlist ?? {});
  return {
    enabled: w.enabled === true && !event?.seating_plan_id,
    mode: w.mode === "manual" ? "manual" : "auto",
    hold_hours: WAITLIST_HOLDS.includes(Number(w.hold_hours) as WaitlistHold) ? Number(w.hold_hours) as WaitlistHold : 12,
    any_timeslot: w.any_timeslot === true,
    before_sale: w.before_sale === true,
    per_ticket_type: w.per_ticket_type === true,
    ask_phone: w.ask_phone === true,
  };
};

export function useWaitlist() {
  const supabase = useSupabaseClient<Database>();
  const eventStore = useEventStore();
  const toast = useToast();
  const { t } = useI18n();
  const { dateTime } = useLocaleFormat();
  const event = computed(() => eventStore.current_event);
  const settings = computed(() => waitlistSettingsOf(event.value));

  // Who waits for a place (and for how many tickets) and who bought after waiting, for the menu,
  // the overview and the capacity notes. Who waits for the sale to open is not counted: they get
  // the opening mail, not a place.
  const counts = useState("waitlist-counts", () => ({ eventId: null as string | null, waiting: 0, tickets: 0, bought: 0 }));
  const loadCounts = async (eventId = event.value?.id) => {
    if (!eventId) return;
    const [{ data: waiting }, { count: bought }] = await Promise.all([
      supabase.from("waitlist_entries").select("quantity").eq("event_id", eventId).eq("status", "waiting").is("sale_opens_at", null),
      supabase.from("waitlist_entries").select("id", { count: "exact", head: true }).eq("event_id", eventId).eq("status", "bought"),
    ]);
    counts.value = { eventId, waiting: waiting?.length ?? 0, tickets: (waiting ?? []).reduce((sum, w) => sum + w.quantity, 0), bought: bought ?? 0 };
  };
  const ownCounts = computed(() => (counts.value.eventId === event.value?.id ? counts.value : null));
  const waitingCount = computed(() => ownCounts.value?.waiting ?? 0);
  const waitingTickets = computed(() => ownCounts.value?.tickets ?? 0);
  const boughtCount = computed(() => ownCounts.value?.bought ?? 0);

  // What sold out, by name, where the shop would now show the waitlist: an upcoming timeslot at its
  // limit, or (with one timeslot, where the counts are per timeslot anyway) a ticket type at its own
  // limit when the waitlist goes per ticket type, else every ticket type
  const soldOutName = ref<string | null>(null);
  // The ticket counts on the event (tickets(count)) include cancelled tickets, vouchers of extras and
  // test tickets, so they only pick the candidates; whether nothing is left is the database's own
  // calculation (get_ticket_availability, the shop's view of timeslot_ticket_places).
  const visibleTypes = (slot: { ticket_type_ids?: string[] | null }) =>
    (event.value?.ticket_types ?? []).filter((tt) => tt.visibility !== "hidden" && tt.kind !== "extra" && tt.ticket_limit !== 0
      && (!slot.ticket_type_ids?.length || slot.ticket_type_ids.includes(tt.id)));
  const unavailable = async (slotId: string) => {
    const { data, error } = await supabase.rpc("get_ticket_availability", { a_event_timeslot_id: slotId });
    return error ? null : new Set((data ?? []).filter((r) => !r.availability).map((r) => r.ticket_type_id));
  };
  const findSoldOut = async (): Promise<string | null> => {
    const e = event.value;
    if (!e || e.seating_plan_id) return null;
    const now = new Date();
    const slots = e.event_timeslots.filter((s) => timeslotClosesAt(s) > now);
    // shortcut: at most five candidates are checked, enough for the one card on the overview
    // A limit of 0 is a timeslot (or ticket type) the organizer closed, not one that sold out: no waitlist for it
    for (const s of slots.filter((s) => !!s.ticket_limit && (s.tickets?.[0]?.count ?? 0) >= s.ticket_limit).slice(0, 5)) {
      const off = await unavailable(s.id);
      const types = visibleTypes(s);
      if (off && types.length && types.every((tt) => off.has(tt.id)))
        // Several timeslots share a title ("Avond"): the date says which one
        return e.event_timeslots.length === 1 ? e.title : [s.title, dateTime(s.start_date)].filter(Boolean).join(' · ');
    }
    if (e.event_timeslots.length !== 1 || !slots.length) return null;
    const types = visibleTypes(slots[0]!);
    if (!types.some((tt) => tt.ticket_limit != null && (tt.tickets?.[0]?.count ?? 0) >= tt.ticket_limit)) return null;
    const off = await unavailable(slots[0]!.id);
    if (!off) return null;
    const fullTypes = types.filter((tt) => off.has(tt.id));
    if (settings.value.per_ticket_type) return fullTypes[0]?.name ?? null;
    return fullTypes.length > 0 && fullTypes.length === types.length ? e.title : null;
  };
  watch(() => [event.value?.id, event.value?.event_timeslots, event.value?.ticket_types, settings.value.per_ticket_type],
    async () => { soldOutName.value = await findSoldOut(); }, { immediate: true });

  // Writes the whole object (or removes it), keeping the activity's other additional settings.
  const save = async (next: WaitlistSettings | null) => {
    if (!event.value) return null;
    const all = { ...(event.value.additional_settings as Record<string, unknown>) };
    if (next) all.waitlist = next;
    else delete all.waitlist;
    const { error } = await supabase.from("events").update({ additional_settings: all as Json }).eq("id", event.value.id);
    if (error) return error;
    //@ts-ignore additional_settings is jsonb
    event.value.additional_settings = all;
    return null;
  };
  // Switching on with the defaults, and saying which
  const switching = ref(false);
  const switchOn = async () => {
    switching.value = true;
    // on from the overview too: then it also joins the activity's menu
    const error = await save(WAITLIST_ON)
      ?? (eventStore.hasModule("waitlist") ? null : await eventStore.saveModules([...eventStore.modules, "waitlist"]));
    switching.value = false;
    if (error) showSupabaseError(toast, error);
    else toast.add({ severity: "success", summary: t("waitlist_switched_on"), detail: t("waitlist_switched_on_detail", { hours: WAITLIST_ON.hold_hours }), life: 6000 });
    return !error;
  };

  return { settings, save, switchOn, switching, loadCounts, waitingCount, waitingTickets, boughtCount, soldOutName };
}
